<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTranslation } from 'i18next-vue'
import { useAppStore } from '@/stores/AppStore'
import { useClinicRepository } from '@/repositories/clinicRepo'
import { MembercardWebapp } from '@/type/MemberCard'
import { useCurrencyFormat } from '@/utils'

const { t } = useTranslation()

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const memberCard = ref<MembercardWebapp | null>(null)
// Tab navigation
const tabs = computed(() => [
  { name: 'PointRedeemCampaignList', label: t('point_redeem_campaign.redeem_tab') },
  { name: 'PointRedeemItemList', label: t('point_redeem_campaign.history_tab') },
])

const isActiveTab = (tabName: string) => {
  // check ว่าตอนนี้กำลังอยู่ที่ tab นั้นหรือไม่
  // (ใช้สำหรับ check เพื่อเปลี่ยน style ของ tab)
  return route.name === tabName
}

const navigateToTab = (tabName: string) => {
  // เปลี่ยน tab
  router.push({ name: tabName })
}

// Fetch patient data
const initPatient = async () => {
  if (appStore.patient) return

  const clinic_repository = useClinicRepository(appStore.clinicUUID)
  const patient = await clinic_repository.fetchPatientMiniInfo()
  appStore.setPatient(patient)
}

// Fetch membercard data
const fetchMemberCard = async () => {
  // ถ้ามีข้อมูลอยู่แล้วก็ไม่ต้อง fetch อีก
  if (appStore.memberCard) {
    memberCard.value = appStore.memberCard
    return
  }

  // ถ้าไม่มีข้อมูลอยู่ ให้ fetch ข้อมูลด้วย patient uuid
  if (appStore.patient?.uuid) {
    await appStore.setMemberCard()
    memberCard.value = appStore.memberCard
  }
}

const formattedPoint = computed(() => {
  // formatted point value
  const point = memberCard.value?.point || 0
  return useCurrencyFormat(Number(point))
})

onMounted(async () => {
  try {
    await initPatient()
    await fetchMemberCard()
  } catch (error) {
    console.error('Error fetching membercard:', error)
  }
})
</script>

<template>
  <div>
    <div class="bg-primary2">
      <van-row align="center" justify="space-around" class="tw-p-[15px]">
        <van-col class="tw-w-full tw-overflow-hidden tw-text-ellipsis" span="5">
          <img src="@/assets/aura_logo.png" alt="Aura Logo" class="tw-w-full tw-h-full" />
        </van-col>

        <van-col span="2"></van-col>

        <van-col span="17" class="tw-flex tw-justify-end tw-items-center tw-gap-3">
          <div class="tw-flex tw-items-center tw-gap-2">
            <div
              class="tw-w-9 tw-h-9 tw-bg-[#ffa726] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-lg"
            >
              <span>P</span>
            </div>
            <span class="tw-text-white tw-font-bold tw-text-xl">{{ formattedPoint }}</span>
          </div>
          <LanguageButton />
        </van-col>
      </van-row>
    </div>

    <van-row justify="center" class="tw-py-4 tw-px-4 tw-gap-3">
      <van-col v-for="tab in tabs" :key="tab.name" span="10">
        <van-button
          round
          block
          :color="isActiveTab(tab.name) ? '#00ccff' : '#e5e7eb'"
          :class="['tw-font-bold', !isActiveTab(tab.name) && 'tw-text-gray-500']"
          @click="navigateToTab(tab.name)"
        >
          {{ tab.label }}
        </van-button>
      </van-col>
    </van-row>

    <div>
      <router-view />
    </div>
  </div>
</template>
